import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors, radius, s16, s8 } from '@ardoq/design-tokens';
import { caption, header4 } from '@ardoq/typography';
import { styled } from 'styled-components';
import { WithPopover } from '@ardoq/popovers';
import { useId } from 'react';
import { FlexBox } from '@ardoq/layout';

type AdvancedNavMenuItemProps = {
  label: string;
  isSelected: boolean;
  isDisabled: boolean;
  iconName: IconName;
  rightContent: React.ReactNode;
  description: string;
  onClick: () => void;
  popoverContent?: string;
  selectedItemIndex: number; // necessary to calculate if a divider between current and next item should be rendered
  currentItemIndex: number; // necessary to calculate if a divider between current and next item should be rendered
};

export const AdvancedNavigationMenuItem = ({
  label,
  isSelected,
  isDisabled,
  iconName,
  rightContent,
  description,
  onClick,
  popoverContent,
  selectedItemIndex,
  currentItemIndex,
}: AdvancedNavMenuItemProps) => {
  const menuItemLabelId = useId();

  if (isSelected) {
    return (
      <li role="presentation">
        <ActiveMenuItemButton
          onClick={onClick}
          disabled={isDisabled}
          role="menuitem"
          aria-labelledby={menuItemLabelId}
        >
          <MenuMainRow
            menuItemLabelId={menuItemLabelId}
            iconName={iconName}
            label={label}
            rightContent={rightContent}
            isActive
          />
          <MenuDescription>{description}</MenuDescription>
        </ActiveMenuItemButton>
      </li>
    );
  }
  const isNextItemSelected = currentItemIndex + 1 === selectedItemIndex;

  return (
    <ListItem $isNextItemSelected={isNextItemSelected}>
      <WithPopover content={popoverContent}>
        <MenuItemButton
          onClick={onClick}
          disabled={isDisabled}
          role="menuitem"
          aria-labelledby={menuItemLabelId}
          aria-disabled={isDisabled}
        >
          <MenuMainRow
            iconName={iconName}
            label={label}
            rightContent={rightContent}
            isActive={false}
            isDisabled={isDisabled}
            menuItemLabelId={menuItemLabelId}
          />
        </MenuItemButton>
      </WithPopover>
    </ListItem>
  );
};

type MenuMainRowProps = {
  iconName: IconName;
  label: string;
  rightContent: React.ReactNode;
  isActive: boolean;
  isDisabled?: boolean;
  /**
   * For accessibility purposes, to link the label with the menu item.
   */
  menuItemLabelId: string;
};

const MenuMainRow = ({
  iconName,
  label,
  rightContent,
  isActive,
  isDisabled = false,
  menuItemLabelId,
}: MenuMainRowProps) => {
  const fontColor = isActive
    ? colors.textModerate
    : isDisabled
      ? colors.textDisabled
      : colors.textSubtle;
  const iconColor = isActive
    ? colors.iconActionBrand
    : isDisabled
      ? colors.iconActionDisabled
      : colors.icon;

  return (
    <FlexBox gap="small" align="center" flexWrap>
      <IconWrapper $iconColor={iconColor}>
        <Icon iconSize={IconSize.MEDIUM} iconName={iconName} />
      </IconWrapper>
      <Label id={menuItemLabelId} $fontColor={fontColor}>
        {label}
      </Label>
      <RightContentWrapper>{rightContent}</RightContentWrapper>
    </FlexBox>
  );
};

const IconWrapper = styled.span<{ $iconColor: string }>`
  color: ${({ $iconColor }) => $iconColor};
`;

const RightContentWrapper = styled.div`
  max-width: 200px;
`;

const Label = styled.span<{ $fontColor: string }>`
  flex-grow: 1;
  color: ${({ $fontColor }) => $fontColor};
  ${header4};
  text-align: start;
  white-space: nowrap;
`;

const MenuItemButton = styled.button`
  padding-left: ${s8};
  padding-right: ${s8};
  padding-top: ${s16};
  padding-bottom: ${s16};
  background-color: transparent;
  width: 100%;
  border: none;
  border: 2px solid transparent;
  border-radius: ${radius.r8};

  /* hovering over the button changes the color of the icon */
  &:not([disabled]):hover,
  &:not([disabled]):focus {
    ${IconWrapper} {
      color: ${colors.iconActionBrandHover};
    }
  }

  &:not([disabled]):focus-visible {
    border-color: ${colors.borderFocus};
  }
`;

const ActiveMenuItemButton = styled(MenuItemButton)`
  background-color: ${colors.surfaceDefaultSubtle};
  border-radius: ${radius.r8};
  padding-bottom: ${s8};
`;

const ListItem = styled.li<{ $isNextItemSelected: boolean }>`
  border: none;

  &:not(:last-of-type) {
    ${({ $isNextItemSelected }) =>
      !$isNextItemSelected &&
      `border-bottom: 1px solid ${colors.borderAction};`}
  }
`;

const MenuDescription = styled.p`
  padding-top: ${s8};
  ${caption};
  text-align: start;
`;
